import { attrValueToPixels, defineCustomElement, getAttribute, getBooleanAttribute, getIntegerAttribute, getReactEventHandler, getRect, NectaryElement, updateAttribute, updateBooleanAttribute, updateExplicitBooleanAttribute, updateIntegerAttribute, subscribeContext, getTargetByAttribute } from '../utils';
const templateHTML = '<style>:host{display:block;outline:0}#listbox{display:flex;flex-direction:row;flex-wrap:wrap;padding:4px 10px;overflow-y:auto}#listbox.empty{display:none}</style><div id="listbox" role="presentation"><slot id="options"></slot></div>';
const NUM_COLS_DEFAULT = 5;
const ITEM_WIDTH = 44;
const ITEM_HEIGHT = 56;
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-color-menu', class extends NectaryElement {
  #$listbox;
  #$optionsSlot;
  #controller = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$listbox = shadowRoot.querySelector('#listbox');
    this.#$optionsSlot = shadowRoot.querySelector('#options');
  }
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const {
      signal
    } = this.#controller;
    const options = {
      signal
    };
    this.role = 'listbox';
    this.tabIndex = 0;
    this.addEventListener('keydown', this.#onListboxKeyDown, options);
    this.addEventListener('blur', this.#onListboxBlur, options);
    this.#$listbox.addEventListener('click', this.#onListboxClick, options);
    this.#$optionsSlot.addEventListener('slotchange', this.#onSlotChange, options);
    this.addEventListener('-change', this.#onChangeReactHandler, options);
    subscribeContext(this, 'keydown', this.#onContextKeyDown, signal);
    subscribeContext(this, 'visibility', this.#onContextVisibility, signal);
    this.#onSlotChange();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['value', 'rows', 'cols'];
  }
  attributeChangedCallback(name) {
    switch (name) {
      case 'value':
        {
          if (this.isDomConnected) {
            this.#onValueChange();
          }
          break;
        }
      case 'rows':
        {
          this.#updateRows();
          break;
        }
      case 'cols':
        {
          if (this.isDomConnected) {
            this.#updateColumns();
          }
          break;
        }
    }
  }
  set value(value) {
    updateAttribute(this, 'value', value);
  }
  get value() {
    return getAttribute(this, 'value', '');
  }
  set colors(value) {
    updateAttribute(this, 'colors', value);
  }
  get colors() {
    return getAttribute(this, 'colors');
  }
  set rows(value) {
    updateIntegerAttribute(this, 'rows', value);
  }
  get rows() {
    return getIntegerAttribute(this, 'rows', null);
  }
  set cols(value) {
    updateIntegerAttribute(this, 'cols', value);
  }
  get cols() {
    return getIntegerAttribute(this, 'cols', null);
  }
  get focusable() {
    return true;
  }
  nthItemRect(index) {
    const $item = this.#getOptionElements()[index];
    if ($item != null) {
      return getRect($item);
    }
    return null;
  }
  #onSlotChange = () => {
    this.#updateColumns();
    this.#onValueChange();
  };
  #updateRows() {
    const rowsValue = getAttribute(this, 'rows');
    this.#$listbox.style.maxHeight = attrValueToPixels(rowsValue, {
      min: 2,
      itemSizeMultiplier: ITEM_HEIGHT
    });
  }
  #updateColumns() {
    const colsValue = getAttribute(this, 'cols');
    const numItems = this.#getOptionElements().length;
    this.#$listbox.style.width = attrValueToPixels(colsValue, {
      min: 1,
      max: numItems,
      defaultValue: Math.min(numItems, NUM_COLS_DEFAULT),
      itemSizeMultiplier: ITEM_WIDTH
    });
  }
  #onListboxBlur = () => {
    this.#selectOption(null);
  };
  #onListboxClick = e => {
    const target = getTargetByAttribute(e, 'value');
    if (target === null) {
      return;
    }
    this.focus();
    this.#dispatchChangeEvent(target);
  };
  #onContextVisibility = e => {
    if (e.detail) {
      this.#selectOption(this.#findCheckedOption());
    } else {
      this.#selectOption(null);
    }
  };
  #onContextKeyDown = e => {
    this.#handleKeydown(e.detail);
  };
  #onListboxKeyDown = e => {
    this.#handleKeydown(e);
  };
  #handleKeydown(e) {
    switch (e.code) {
      case 'Space':
      case 'Enter':
        {
          e.preventDefault();
          const $option = this.#findSelectedOption();
          if ($option !== null) {
            this.#dispatchChangeEvent($option);
          }
          break;
        }
      case 'ArrowLeft':
        {
          e.preventDefault();
          this.#selectOption(this.#getPrevOption());
          break;
        }
      case 'ArrowRight':
        {
          e.preventDefault();
          this.#selectOption(this.#getNextOption());
          break;
        }
      case 'ArrowDown':
        {
          e.preventDefault();
          this.#selectOption(this.#getNextRowOption());
          break;
        }
      case 'ArrowUp':
        {
          e.preventDefault();
          this.#selectOption(this.#getPrevRowOption());
          break;
        }
    }
  }
  #onValueChange() {
    const value = this.value;
    for (const $option of this.#getOptionElements()) {
      const isChecked = value === getAttribute($option, 'value', '');
      updateBooleanAttribute($option, 'data-checked', isChecked);
      updateBooleanAttribute($option, 'data-selected', isChecked);
      updateExplicitBooleanAttribute($option, 'aria-selected', isChecked);
    }
  }
  #getFirstOption() {
    const $options = this.#getOptionElements();
    return $options[0] ?? null;
  }
  #getLastOption() {
    const $options = this.#getOptionElements();
    return $options[$options.length - 1] ?? null;
  }
  #getNextOption() {
    const index = this.#getSelectedOptionIndex();
    if (index !== null) {
      const $options = this.#getOptionElements();
      return $options[(1 + index) % $options.length];
    }
    return this.#getFirstOption();
  }
  #getPrevOption() {
    const index = this.#getSelectedOptionIndex();
    if (index !== null) {
      const $options = this.#getOptionElements();
      return $options[(index - 1 + $options.length) % $options.length];
    }
    return this.#getLastOption();
  }
  #getNextRowOption() {
    const selectedIndex = this.#getSelectedOptionIndex();
    if (selectedIndex !== null) {
      const $options = this.#getOptionElements();
      const numCols = Math.min(this.cols ?? NUM_COLS_DEFAULT, $options.length);
      const numColsInLastRow = $options.length % numCols;
      if (numColsInLastRow > 0) {
        if (selectedIndex < $options.length - numCols) {
          return $options[selectedIndex + numCols];
        }
        if (numColsInLastRow > 0 && selectedIndex < $options.length - numColsInLastRow) {
          return $options[$options.length - 1];
        }
        return $options[selectedIndex + numColsInLastRow - $options.length];
      }
      return $options[(selectedIndex + numCols) % $options.length];
    }
    return this.#getFirstOption();
  }
  #getPrevRowOption() {
    const selectedIndex = this.#getSelectedOptionIndex();
    if (selectedIndex !== null) {
      const $options = this.#getOptionElements();
      const numCols = Math.min(this.cols ?? NUM_COLS_DEFAULT, $options.length);
      const numColsInLastRow = $options.length % numCols;
      if (selectedIndex < numColsInLastRow) {
        return $options[(selectedIndex - numColsInLastRow + $options.length) % $options.length];
      }
      if (selectedIndex < numCols) {
        return $options[(selectedIndex - numCols - numColsInLastRow + $options.length) % $options.length];
      }
      return $options[(selectedIndex - numCols + $options.length) % $options.length];
    }
    return this.#getFirstOption();
  }
  #selectOption($option) {
    const hasRows = this.hasAttribute('rows');
    for (const $op of this.#getOptionElements()) {
      const isSelected = $op === $option;
      updateBooleanAttribute($op, 'data-selected', isSelected);
      if (isSelected && hasRows) {
        $op.scrollIntoView?.({
          block: 'nearest'
        });
      }
    }
  }
  #getOptionElements() {
    return this.#$optionsSlot.assignedElements();
  }
  #getSelectedOptionIndex() {
    const elements = this.#getOptionElements();
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (getBooleanAttribute(el, 'data-selected')) {
        return i;
      }
    }
    return null;
  }
  #findSelectedOption() {
    const elements = this.#getOptionElements();
    for (const el of elements) {
      if (getBooleanAttribute(el, 'data-selected')) {
        return el;
      }
    }
    return null;
  }
  #findCheckedOption() {
    const elements = this.#getOptionElements();
    const value = this.value;
    for (const $el of elements) {
      if (getAttribute($el, 'value') === value) {
        return $el;
      }
    }
    return null;
  }
  #dispatchChangeEvent($opt) {
    const value = getAttribute($opt, 'value', '');
    this.dispatchEvent(new CustomEvent('-change', {
      detail: value
    }));
  }
  #onChangeReactHandler = e => {
    getReactEventHandler(this, 'on-change')?.(e);
  };
});